import {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';
import { Network, Plus, Share2, Trash2, Waypoints } from 'lucide-react';

import { PageTitle } from '@/components/page-title';
import { Pagination } from '@/components/pagination';
import { ShareModal } from '@/components/share-modal';
import { ContextGraphViewerModal } from '@/components/context-graph-viewer/context-graph-viewer-modal';
import {
  VIEW_GRAPH_PARAM,
  VIEW_STATE_PARAM,
} from '@/components/context-graph-viewer/helpers/view-state';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { Modal } from '@/components/modal';
import { useNotification } from '@/components/notifications';
import { AuthContext } from '@/context/auth-context';
import { useOrgStore } from '@/stores/org-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useContextGraphs } from '@/hooks/queries/use-context-graphs';
import { useCreateContextGraph } from '@/hooks/mutations/context-graphs/use-create-context-graph';
import { useDeleteContextGraph } from '@/hooks/mutations/context-graphs/use-delete-context-graph';
import { formatBytes } from '@/utils/format-bytes';
import { ContextGraphListItem } from '@/types/context-graph-types';

const ITEMS_PER_PAGE = 10;

export const ContextGraphsPage: FC = () => {
  const notification = useNotification();
  const { user, isAdmin } = useContext(AuthContext);
  const orgId = useOrgStore((state) => state.currentOrg?.orgid ?? null);
  const [shareGraph, setShareGraph] = useState<ContextGraphListItem | null>(
    null,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [graphToDelete, setGraphToDelete] =
    useState<ContextGraphListItem | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: graphs = [], isLoading, isError } = useContextGraphs();

  const totalPages = Math.max(1, Math.ceil(graphs.length / ITEMS_PER_PAGE));
  // Clamp instead of resetting state so deleting the last row of the final
  // page lands on the new last page without an extra render of stale rows.
  const safePage = Math.min(currentPage, totalPages);
  const pagedGraphs = useMemo(
    () =>
      graphs.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE),
    [graphs, safePage],
  );

  const createGraph = useCreateContextGraph({
    onSuccess: () => {
      setIsCreateOpen(false);
      setNewName('');
      setNewDescription('');
      notification.success('Context graph created');
    },
    onError: () => notification.error('Failed to create context graph'),
  });

  const deleteGraph = useDeleteContextGraph({
    onSuccess: () => {
      setGraphToDelete(null);
      notification.success('Context graph deleted');
    },
    onError: () => notification.error('Failed to delete context graph'),
  });

  // The open viewer is derived from the URL (?viewGraph=<id>), never copied
  // into state — deep links, share links, and in-app opens all converge here.
  // The viewer's own `vs` view-state sync writes via history.replaceState,
  // which react-router doesn't observe, so this derivation stays stable while
  // panning. Don't call setSearchParams while the viewer is open except in
  // closeViewer — router-held params are stale relative to window.location.
  const viewGraphId = searchParams.get(VIEW_GRAPH_PARAM);
  const viewGraph = useMemo(
    () =>
      viewGraphId
        ? (graphs.find((g) => g.graph_id === viewGraphId) ?? null)
        : null,
    [graphs, viewGraphId],
  );

  const openViewer = useCallback(
    (graph: ContextGraphListItem) => {
      setSearchParams(
        (prev) => {
          prev.set(VIEW_GRAPH_PARAM, graph.graph_id);
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const closeViewer = useCallback(() => {
    setSearchParams(
      (prev) => {
        prev.delete(VIEW_GRAPH_PARAM);
        prev.delete(VIEW_STATE_PARAM);
        return prev;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // Deep link points at a graph that no longer exists (deleted/revoked).
  const staleViewParam =
    !isLoading && !isError && Boolean(viewGraphId) && !viewGraph;
  useEffect(() => {
    if (staleViewParam) {
      closeViewer();
    }
  }, [staleViewParam, closeViewer]);

  const handleCreate = () => {
    if (!newName.trim()) {
      notification.error('Name is required');
      return;
    }
    createGraph.mutate({
      name: newName.trim(),
      description: newDescription.trim(),
    });
  };

  // Owner-only actions (Share/Delete). user_role is only populated on the
  // ACL-filtered /public list; the internal/M2M list returns it as null, so
  // fall back to matching the backfilled owner_email against the signed-in
  // user (admins can always manage).
  const canManage = (g: ContextGraphListItem): boolean => {
    if (isAdmin) return true;
    if (g.user_role === 'owner') return true;
    const email = user?.email?.toLowerCase();
    return Boolean(email && g.owner_email?.toLowerCase() === email);
  };

  // Editors can curate graph contents (remove entities/facts) but not
  // share/delete the graph itself.
  const canEdit = (g: ContextGraphListItem): boolean =>
    canManage(g) || g.user_role === 'editor';

  let listContent: ReactNode;
  if (isLoading) {
    listContent = (
      <div className='text-muted-foreground py-12 text-center text-sm'>
        Loading context graphs…
      </div>
    );
  } else if (isError) {
    listContent = (
      <div className='text-muted-foreground py-12 text-center text-sm'>
        Couldn&apos;t load context graphs. Try reloading the page.
      </div>
    );
  } else if (graphs.length === 0) {
    listContent = (
      <div className='text-muted-foreground flex flex-col items-center gap-3 py-16 text-center'>
        <Network className='size-8' />
        <p>No context graphs yet. Create one to get started.</p>
      </div>
    );
  } else {
    listContent = (
      <Table tableRef={tableRef}>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Owner</TableHead>
            <TableHead>Entities</TableHead>
            <TableHead>Facts</TableHead>
            <TableHead>Episodes</TableHead>
            <TableHead>Size</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead className='w-24' />
          </TableRow>
        </TableHeader>
        <TableBody>
          {pagedGraphs.map((g) => (
            <TableRow key={g.graph_id}>
              <TableCell className='font-medium'>{g.name}</TableCell>
              <TableCell className='text-muted-foreground max-w-xs truncate'>
                {g.description}
              </TableCell>
              <TableCell className='text-muted-foreground'>
                {g.owner_email ?? '—'}
              </TableCell>
              <TableCell>{g.stats?.node_count ?? 0}</TableCell>
              <TableCell>{g.stats?.edge_count ?? 0}</TableCell>
              <TableCell>{g.stats?.episode_count ?? 0}</TableCell>
              <TableCell className='text-muted-foreground'>
                {formatBytes(g.stats?.size_bytes)}
              </TableCell>
              <TableCell className='text-muted-foreground'>
                {new Date(g.updated_at).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <div className='flex items-center gap-1'>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant='ghost'
                        size='icon'
                        onClick={() => openViewer(g)}
                        aria-label='View graph'
                      >
                        <Waypoints className='size-4' />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>View graph</TooltipContent>
                  </Tooltip>
                  {canManage(g) ? (
                    <>
                      <Button
                        variant='ghost'
                        size='icon'
                        onClick={() => setShareGraph(g)}
                        aria-label='Manage sharing'
                      >
                        <Share2 className='size-4' />
                      </Button>
                      <Button
                        variant='ghost'
                        size='icon'
                        onClick={() => setGraphToDelete(g)}
                        aria-label='Delete context graph'
                      >
                        <Trash2 className='size-4' />
                      </Button>
                    </>
                  ) : null}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <div className='w-full'>
      <div className='mb-6 flex items-center justify-between'>
        <PageTitle title='Context Graphs' />
        <Button onClick={() => setIsCreateOpen(true)}>
          <Plus className='mr-2 size-4' />
          New Context Graph
        </Button>
      </div>

      {listContent}

      {!isLoading && !isError && totalPages > 1 && (
        <div className='mt-4'>
          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      <Modal
        title='New Context Graph'
        description="Create a private context graph. You can share it with teammates after it's created."
        isDialogOpen={isCreateOpen}
        setIsDialogOpen={(open) => {
          setIsCreateOpen(open);
          if (!open) {
            setNewName('');
            setNewDescription('');
          }
        }}
        footer={
          <>
            <Button
              variant='outline'
              onClick={() => setIsCreateOpen(false)}
              disabled={createGraph.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createGraph.isPending}>
              {createGraph.isPending ? 'Creating…' : 'Create'}
            </Button>
          </>
        }
      >
        <div className='flex flex-col gap-4 py-2'>
          <div className='flex flex-col gap-1.5'>
            <label className='text-sm font-medium' htmlFor='cg-name'>
              Name
            </label>
            <Input
              id='cg-name'
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder='e.g. EY Engagement Knowledge'
            />
          </div>
          <div className='flex flex-col gap-1.5'>
            <label className='text-sm font-medium' htmlFor='cg-description'>
              Description
            </label>
            <Textarea
              id='cg-description'
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder='What this graph is for'
            />
          </div>
        </div>
      </Modal>

      <DeleteConfirmationModal
        title={`Delete ${graphToDelete?.name ?? 'context graph'}?`}
        description='This permanently deletes the graph and its data.'
        buttonLabel='Delete'
        isLoading={deleteGraph.isPending}
        isOpen={graphToDelete !== null}
        handleDelete={() => {
          if (graphToDelete) {
            deleteGraph.mutate(graphToDelete.graph_id);
          }
        }}
        handleCancel={() => setGraphToDelete(null)}
      />

      {viewGraph ? (
        <ContextGraphViewerModal
          graph={viewGraph}
          open={viewGraph !== null}
          canEdit={canEdit(viewGraph)}
          onOpenChange={(open) => {
            if (!open) {
              closeViewer();
            }
          }}
        />
      ) : null}

      {shareGraph ? (
        <ShareModal
          resourceType='context-graph'
          resourceId={shareGraph.graph_id}
          orgId={orgId}
          open={shareGraph !== null}
          onOpenChange={(open) => {
            if (!open) {
              setShareGraph(null);
            }
          }}
        />
      ) : null}
    </div>
  );
};
